// Users Configuration - UNIFIED APPROACH
// Manages user accounts and authentication
// Based on Prisma ad_User model schema

import { createTrackingConfig } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import {
  enrichUserWithUiPreferences,
  buildUiPreferencesPayload,
  UI_COLOR_THEME_OPTIONS,
  UI_TABLE_UI_THEME_OPTIONS,
} from './userUiPreferencesAdmin';

export const usersConfig = {
  entityName: 'User',

  enrichEntity: enrichUserWithUiPreferences,
  
  // Entity information for file attachments (if needed in future)
  entityTypeId: null, // Users don't typically have file attachments
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/users',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // User-specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      resetPassword: (id) => `/${id}/reset-password`,
      changePassword: (id) => `/${id}/change-password`,
      profile: (id) => `/${id}/profile`,
      byRole: (roleId) => `/role/${roleId}`,
      active: '/active',
      inactive: '/inactive',
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      roles: '/role',
      userDepartments: '/user-department'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/users',
      active: '/users/active',
      inactive: '/users/inactive'
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '700px', // Custom for users (smaller than default)
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
    },

  // Features configuration
  features: {
    fileAttachments: false, // Disable file attachment functionality for users
  },
  
  // Custom handlers for Users-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in usersPage
    onDelete: null, // Will be defined in usersPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in usersPage
    
    // User-specific actions
    onActivate: null, // Will handle user activation
    onDeactivate: null, // Will handle user deactivation
    onPasswordReset: null, // Will handle password reset
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      // Users might not need board functionality, but keeping structure consistent
      console.log('Board drop not implemented for users');
    }
  },
  
  // Service configuration
  service: {
    name: 'usersService',
    methods: {
      getAll: 'getAllUsers',
      getById: 'getUserById', 
      create: 'createUser',
      update: 'updateUser',
      delete: 'deleteUser',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'User Management',
  description: 'User account and access management system for application security',
  icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'createdAt', 'updatedAt', 'createdById', 'updatedById',
    // File attachment meta that should never be sent to backend for users
    'fileAttachmentCount', 'fileAttachments',
    'resetPasswordToken', 'resetPasswordExpires',
    'uiColorTheme', 'uiTableUiTheme',
    'uiShowSidebarGroups', 'uiShowSidebarSearch', 'uiShowSidebarFavorites',
    'uiColorThemeDisplay', 'uiTableUiThemeDisplay', 'uiSidebarLayoutDisplay',
    // Remove all relation fields
    'role', 'users_active_sessions', 'approvals', 'createdDocuments', 'updatedDocuments',
    'userDepartment',
    'archivedDocuments', 'deprecatedDocuments', 'publishedDocuments',
    'uploadedRevisions', 'revisionsIssued', 'createdRevisions', 'updatedRevisions',
    'deletedRevisions', 'timelineEntries', 'AuditedLogs', 'changeRequest',
    'changeApprovals', 'changeTimelineEntries', 'changeRequestsCreated',
    'changeRequestsUpdated', 'changeRequestsClosed'
  ],

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'userEmail', 
      label: 'Email Address', 
      type: 'text', 
      required: true,
      placeholder: 'user@example.com',
      table: { header: 'Email', width: '250px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        email: true,
        maxLength: 255,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address'
      },
      autoComplete: 'email'
    },
    { 
      name: 'userFirstName', 
      label: 'First Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter first name',
      table: { header: 'First Name', width: '150px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        message: 'First name is required'
      },
      autoComplete: 'given-name'
    },
    { 
      name: 'userLastName', 
      label: 'Last Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter last name',
      table: { header: 'Last Name', width: '150px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        message: 'Last name is required'
      },
      autoComplete: 'family-name'
    },
    { 
      name: 'userDisplayName', 
      label: 'Display Name', 
      type: 'text', 
      required: false,
      placeholder: 'Enter display name (optional)',
      table: { header: 'Display Name', width: '180px', sortable: true },
      filterable: true,
      validation: { 
        required: false, 
        message: 'Display name is required'
      },
      helperText: 'Leave empty to auto-generate from first and last name'
    },
    { 
      name: 'userPassword', 
      label: 'Password', 
      type: 'password', 
      required: false, // Custom validation will handle this
      placeholder: 'Enter secure password',
      table: false, // Never show passwords in table
      filterable: false,
      validation: false, // Disable all built-in validation - custom validation only
      autoComplete: 'new-password',
      helperText: 'Leave empty to keep current password, or enter new password to change'
    },
    { 
      name: 'roleId', 
      label: 'Role', 
      type: 'select', 
      required: false,
      placeholder: 'Select user role',
      table: { 
        header: 'Role', 
        width: '120px', 
        sortable: true, 
        displayField: 'role',
        cellRenderer: 'badge'
      },
      filterable: true,
      lookupEndpoint: 'roles',
      validation: { 
        required: false,
        message: 'Role is required'
      }
    },
    {
      name: 'departmentId',
      label: 'Department Name',
      type: 'select',
      required: true,
      placeholder: 'Select user department',
      lookupEndpoint: 'userDepartments',
      options: [],
      table: {
        header: 'Department Name',
        width: '180px',
        sortable: true,
        displayField: 'userDepartment',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        min: 1,
        message: 'User department is required'
      }
    },
    { 
      name: 'userIsActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'User account is active and can log in',
      description: 'Inactive users cannot access the system'
    },
    {
      name: 'isQMS',
      label: 'QMS',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'QMS',
        width: '90px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Enable QMS access for this user'
    },
    {
      name: 'isQMS_PSC',
      label: 'QMS PSC',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'QMS PSC',
        width: '110px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Enable Product Status Change access in QMS'
    },
    {
      name: 'isTicketing',
      label: 'Ticketing',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Ticketing',
        width: '110px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Enable ticketing access for this user'
    },
    {
      name: 'isIT',
      label: 'IT',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'IT',
        width: '80px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Mark user as IT assignee for ticketing'
    },
    {
      name: 'uiColorThemeDisplay',
      label: 'Χρωματικό θέμα',
      type: 'text',
      readOnly: true,
      table: {
        header: 'Θέμα',
        width: '140px',
        sortable: true,
        displayField: 'uiColorThemeDisplay',
      },
      filterable: false,
    },
    {
      name: 'uiTableUiThemeDisplay',
      label: 'Πίνακες',
      type: 'text',
      readOnly: true,
      table: {
        header: 'Πίνακες',
        width: '100px',
        sortable: true,
        displayField: 'uiTableUiThemeDisplay',
      },
      filterable: false,
    },
    {
      name: 'uiColorTheme',
      label: 'Χρωματικό θέμα',
      type: 'select',
      required: false,
      placeholder: 'Προεπιλογή οργανισμού',
      options: UI_COLOR_THEME_OPTIONS,
      table: false,
      filterable: false,
      validation: { required: false },
      helperText: 'Όπως στο Profile → Εμφάνιση. Κενό = προεπιλογή.',
    },
    {
      name: 'uiTableUiTheme',
      label: 'Εμφάνιση πινάκων',
      type: 'select',
      required: false,
      placeholder: 'Προεπιλογή οργανισμού',
      options: UI_TABLE_UI_THEME_OPTIONS,
      table: false,
      filterable: false,
      validation: { required: false },
      helperText: 'Κλασικό ή Νέο — αποθηκεύεται στον λογαριασμό του χρήστη.',
    },
    {
      name: 'uiShowSidebarGroups',
      label: 'Ομάδες sidebar',
      type: 'switch',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
      switchLabel: 'Εμφάνιση τίτλων ενοτήτων στο μενού',
    },
    {
      name: 'uiShowSidebarSearch',
      label: 'Αναζήτηση μενού',
      type: 'switch',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
      switchLabel: 'Εμφάνιση πεδίου αναζήτησης στο sidebar',
    },
    {
      name: 'uiShowSidebarFavorites',
      label: 'Αγαπημένα sidebar',
      type: 'switch',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
      switchLabel: 'Εμφάνιση αγαπημένων και καρφιτσώματος',
    },
    {
      name: 'uiSidebarLayoutDisplay',
      label: 'Sidebar',
      type: 'text',
      readOnly: true,
      table: {
        header: 'Sidebar',
        width: '160px',
        sortable: false,
        displayField: 'uiSidebarLayoutDisplay',
      },
      filterable: false,
    },

    // Password Reset Fields (read-only)
    { 
      name: 'resetPasswordToken', 
      label: 'Password Reset Token', 
      type: 'text', 
      readOnly: true,
      table: false,
      filterable: false,
      validation: { required: false }
    },
    { 
      name: 'resetPasswordExpires', 
      label: 'Password Reset Expires', 
      type: 'date', 
      readOnly: true,
      table: false,
      filterable: false,
      validation: { required: false }
    },

    // System Fields (read-only)
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readOnly: true,
      table: { 
        header: 'Created', 
        width: '120px', 
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: false,
      validation: { required: false }
    },
    { 
      name: 'updatedAt', 
      label: 'Updated Date', 
      type: 'date', 
      readOnly: true,
      table: { 
        header: 'Last Updated', 
        width: '120px', 
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: false,
      validation: { required: false }
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
    
    return columns;
  },

  exportFileName: 'qms_users',

  // Backend field mappings
  fieldMappings: {
    // Map frontend display fields to backend field paths
    role: 'role.name',
    userDepartment: 'userDepartment.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'userFirstName', 'userLastName', 'userEmail', 'userIsActive', 'isQMS', 'isQMS_PSC', 'isTicketing', 'roleId', 'departmentId'
        , 'isIT'
      ]
    },
    {
      name: 'advanced',
      label: 'Advanced',
      icon: '⚙️',
      fields: [
        'userDisplayName', 'createdAt', 'updatedAt'
      ]
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    userIsActive: [true, false], // Active users first
    roleId: ['admin', 'manager', 'user', 'guest'] // Role hierarchy
  },

  // Messages Configuration
  messages: {
    loading: 'Loading users...',
    noData: 'No users found. Click "Add User" to create the first user account.',
    loadError: 'Failed to load users',
    saveSuccess: 'User saved successfully!',
    saveError: 'Failed to save user',
    deleteSuccess: 'User deleted successfully!',
    deleteError: 'Failed to delete user',
    deleteConfirm: 'Are you sure you want to delete this user? This action cannot be undone and will remove all associated data.',
    activateSuccess: 'User activated successfully!',
    deactivateSuccess: 'User deactivated successfully!',
    passwordResetSuccess: 'Password reset email sent successfully!'
  },

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Personal Information',
      icon: '👤',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['userFirstName', 'userLastName', 'userDisplayName', 'userEmail'],
      fieldSpans: {
        'userFirstName': 'md:col-span-1',
        'userLastName': 'md:col-span-1',
        'userDisplayName': 'md:col-span-1',
        'userEmail': 'md:col-span-1'
      }
    },
    {
      title: 'Access & Security',
      icon: '🔐',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['userPassword', 'roleId', 'departmentId', 'userIsActive', 'isQMS', 'isQMS_PSC', 'isTicketing', 'isIT'],
      fieldSpans: {
        'userPassword': 'md:col-span-1',
        'roleId': 'md:col-span-1',
        'departmentId': 'md:col-span-1',
        'userIsActive': 'md:col-span-1',
        'isQMS': 'md:col-span-1',
        'isQMS_PSC': 'md:col-span-1',
        'isTicketing': 'md:col-span-1',
        'isIT': 'md:col-span-1'
      }
    },
    {
      title: 'Εμφάνιση',
      icon: '🎨',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: [
        'uiColorTheme',
        'uiTableUiTheme',
        'uiShowSidebarGroups',
        'uiShowSidebarSearch',
        'uiShowSidebarFavorites',
      ],
      fieldSpans: {
        uiColorTheme: 'md:col-span-1',
        uiTableUiTheme: 'md:col-span-1',
        uiShowSidebarGroups: 'md:col-span-1',
        uiShowSidebarSearch: 'md:col-span-1',
        uiShowSidebarFavorites: 'md:col-span-1',
      },
    },
  ],

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('User'),

  // Labels and Text Configuration (uses predefined User labels + custom overrides)
  labels: {
    // Using predefined User labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'User',
    plural: 'Users',
    pageTitle: 'User Management',
    pageSubtitle: 'Manage user accounts and access permissions',
    searchPlaceholder: 'Search users by name or email...',
    viewTitle: 'View User',
    noSelectionText: 'Select a user to see details',
    
    // Action labels
    activateUser: 'Activate User',
    deactivateUser: 'Deactivate User',
    resetPassword: 'Reset Password',
    changePassword: 'Change Password',
    viewProfile: 'View Profile'
  },

  // Custom validation function for handling conditional field requirements
  validateField: (fieldName, value, formData, formMode) => {
    // Handle conditional password validation
    if (fieldName === 'userPassword') {
      // Edit mode: password is optional (empty means keep current password)
      if (formMode === 'edit') {
        if (!value || value.trim() === '') {
          return null; // Empty password is valid in edit mode - keeps current password
        }
        // If password is provided in edit mode, validate strength
        if (value.length < 8) {
          return 'Password must be at least 8 characters long';
        }
        if (value.length > 128) {
          return 'Password must not exceed 128 characters';
        }
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(value)) {
          return 'Password must contain uppercase, lowercase, number, and special character';
        }
        return null;
      }
      
      // Create mode: password is required
      if (formMode === 'add') {
        if (!value || value.trim() === '') {
          return 'Password is required for new users';
        }
        // Validate password strength in create mode
        if (value.length < 8) {
          return 'Password must be at least 8 characters long';
        }
        if (value.length > 128) {
          return 'Password must not exceed 128 characters';
        }
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(value)) {
          return 'Password must contain uppercase, lowercase, number, and special character';
        }
        return null;
      }
    }
    
    // All other fields: no custom validation (handled by generic system)
    return null;
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Ensure file attachment meta is never sent (even if present in formData)
    ['fileAttachmentCount', 'fileAttachments'].forEach(field => {
      if (Object.prototype.hasOwnProperty.call(transformed, field)) {
        delete transformed[field];
      }
    });

    // Handle password field - remove if empty (for edit mode, keeps current password)
    if (transformed.userPassword === '' || transformed.userPassword === null || transformed.userPassword === undefined) {
      delete transformed.userPassword;
    }

    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = ['roleId', 'departmentId'];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Handle boolean fields
    if (transformed.userIsActive !== undefined) {
      if (typeof transformed.userIsActive === 'string') {
        transformed.userIsActive = transformed.userIsActive === 'true';
      } else {
        transformed.userIsActive = Boolean(transformed.userIsActive);
      }
    }

    if (transformed.isQMS !== undefined) {
      if (typeof transformed.isQMS === 'string') {
        transformed.isQMS = transformed.isQMS === 'true';
      } else {
        transformed.isQMS = Boolean(transformed.isQMS);
      }
    }

    if (transformed.isQMS_PSC !== undefined) {
      if (typeof transformed.isQMS_PSC === 'string') {
        transformed.isQMS_PSC = transformed.isQMS_PSC === 'true';
      } else {
        transformed.isQMS_PSC = Boolean(transformed.isQMS_PSC);
      }
    }

    if (transformed.isTicketing !== undefined) {
      if (typeof transformed.isTicketing === 'string') {
        transformed.isTicketing = transformed.isTicketing === 'true';
      } else {
        transformed.isTicketing = Boolean(transformed.isTicketing);
      }
    }

    if (transformed.isIT !== undefined) {
      if (typeof transformed.isIT === 'string') {
        transformed.isIT = transformed.isIT === 'true';
      } else {
        transformed.isIT = Boolean(transformed.isIT);
      }
    }

    const uiPrefs = buildUiPreferencesPayload(transformed);
    if (uiPrefs) {
      transformed.uiPreferences = uiPrefs;
    }
    delete transformed.uiColorTheme;
    delete transformed.uiTableUiTheme;
    delete transformed.uiShowSidebarGroups;
    delete transformed.uiShowSidebarSearch;
    delete transformed.uiShowSidebarFavorites;
    delete transformed.uiColorThemeDisplay;
    delete transformed.uiTableUiThemeDisplay;
    delete transformed.uiSidebarLayoutDisplay;

    return transformed;
  },

  // Help text for field descriptions
  helpText: {
    userEmail: { short: 'Email address used for login and system notifications. Must be unique across all users.' },
    userFirstName: { short: 'User\'s first name for identification and display purposes.' },
    userLastName: { short: 'User\'s last name for identification and display purposes.' },
    userDisplayName: { short: 'Custom display name shown in the system. If empty, will auto-generate from first and last name.' },
    userPassword: { short: 'Secure password for user authentication. Leave empty when editing to keep current password.' },
    roleId: { short: 'User role that determines system permissions and access levels.' },
    departmentId: { short: 'User department assigned to this user account.' },
    userIsActive: { short: 'Whether the user account is active and can log into the system.' },
    isQMS: { short: 'Whether the user has access to the QMS module.' },
    isQMS_PSC: { short: 'Whether the user has access to Product Status Change features in QMS.' },
    isTicketing: { short: 'Whether the user has access to the Ticketing module.' },
    isIT: { short: 'Whether the user is available as an IT assignee in Ticketing.' },
    uiColorTheme: { short: 'Χρωματικό θέμα interface (sidebar, header). Κενό = προεπιλογή.' },
    uiTableUiTheme: { short: 'Κλασικό ή Νέο για πίνακες και badges.' },
    uiShowSidebarGroups: { short: 'Τίτλοι ενοτήτων (QMS, Admin, …) στο sidebar.' },
    uiShowSidebarSearch: { short: 'Πεδίο αναζήτησης στο sidebar.' },
    uiShowSidebarFavorites: { short: 'Ενότητα και καρφίτσωμα αγαπημένων.' },
    resetPasswordToken: { short: 'System-generated token for password reset functionality (read-only).' },
    resetPasswordExpires: { short: 'Expiration date for password reset token (read-only).' },
    createdAt: { short: 'Date and time when the user account was created (read-only).' },
    updatedAt: { short: 'Date and time when the user account was last modified (read-only).' }
  }
};
